import { useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Switch } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Trash } from "lucide-react";
import moment from "moment";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { DataLoadError } from "components/ui/DataLoadError";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Select } from "components/ui/Select/Select";
import { useCreatePrescription } from "features/medications/hooks/useCreatePrescription";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useDrugAllergies } from "features/patients/hooks/useDrugAllergies";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { deriveFinishDate } from "utils/medicationDates";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./AddPrescriptionForm.module.css";
import { SelectDrug } from "./SelectDrug/SelectDrug";
import { downloadPrescription, medicineSchema, printPrescription } from "./utils";

const formSchema = z.object({
  medicines: z.array(medicineSchema),
  instructions: z.string(),
});

export const AddPrescriptionForm = ({
  consultationId,
  onSuccess,
  onSaved,
  onCancel,
  initialData = {},
  autoSave = false,
  initialMedications,
  isRenewal = false,
  isReadOnly = false,
}) => {
  const [hasFinishDate, setHasFinishDate] = useState(() => {
    const rawDate = initialData.medicines?.[0]?.finishDate;
    if (!rawDate) return false;
    return moment(rawDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid();
  });
  const [medicationAllergies, setMedicationAllergies] = useState({});
  // Drugs whose allergy check could not be completed, and the prescriber's
  // acknowledgement of that (KIN-1615).
  const [failedAllergyChecks, setFailedAllergyChecks] = useState({});
  const [
    hasAcknowledgedUncheckedAllergies,
    setHasAcknowledgedUncheckedAllergies,
  ] = useState(false);

  const savedId = useRef(initialData?.id || null);

  const queryClient = useQueryClient();
  const patientId = useCurrentPatientId();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const createPrescription = useCreatePrescription();
  const {
    data: drugAllergies,
    isError: isDrugAllergiesError,
    isFetching: isRefetchingDrugAllergies,
    refetch: refetchDrugAllergies,
  } = useDrugAllergies(patientId);
  const { data: practiceSettings } = usePracticeSettings();
  const notesEnabled = !!practiceSettings?.medicationNotesEnabled;

  // Handler for when a drug is selected and checked for allergies.
  // `checkState.didFail` distinguishes "checked, none found" from "could not
  // check" — both arrive here with an empty list (KIN-1615).
  const handleAllergyCheck = (index, drugId, allergies, checkState = {}) => {
    setMedicationAllergies((prev) => {
      const updated = { ...prev };
      if (!drugId || allergies.length === 0) {
        delete updated[index];
      } else {
        updated[index] = { drugId, allergies };
      }
      return updated;
    });

    setFailedAllergyChecks((prev) => {
      const updated = { ...prev };
      if (checkState.didFail) {
        updated[index] = {
          drugName: checkState.drug?.tradeName ?? "this medication",
          retry: checkState.retry,
        };
      } else {
        delete updated[index];
      }
      return updated;
    });
  };

  const uncheckedDrugNames = Object.values(failedAllergyChecks).map(
    (entry) => entry.drugName
  );
  const retryFailedAllergyChecks = () =>
    Object.values(failedAllergyChecks).forEach((entry) => entry.retry?.());
  // Fail-open is the dangerous default on this screen, so an incomplete check
  // has to be acknowledged before the prescription can be saved.
  const needsAllergyAcknowledgement =
    !isReadOnly && (uncheckedDrugNames.length > 0 || isDrugAllergiesError);
  const isBlockedOnAllergyCheck =
    needsAllergyAcknowledgement && !hasAcknowledgedUncheckedAllergies;

  // Get all allergies from selected medications (flattened and deduplicated)
  const selectedMedicationAllergies = Object.values(medicationAllergies)
    .flatMap((entry) => entry.allergies)
    .filter(
      (allergy, index, self) =>
        index === self.findIndex((a) => a.id === allergy.id)
    );

  const getDefaultMedicine = () => ({
    dosage: "As prescribed",
    form: "Tablet",
    startDate: moment().format("DD-MM-YYYY"),
    // Derived from the duration, and empty until there is one — never today, which stored a course that
    // finished on the day it was issued and now reads as discontinued (KIN-1932).
    finishDate: "",
    duration: "",
    durationFormat: "days",
    quantity: "",
    usageType: "ACUTE",
    repeat: "",
    route: "",
    frequency: "",
    paymentType: "",
    isManual: false,
    tradeName: "",
    manufacturer: "",
    packSize: "",
    medicineId: "",
    note: "",
  });

  const prepareMedicationsForRenewal = () => {
    if (isRenewal && initialMedications && initialMedications.length > 0) {
      return initialMedications.map((med) => ({
        ...med,
        // Only manual when the original had no drug-catalog link; otherwise keep the catalog
        // link so submit sends medicationId instead of silently falling back to free-text fields.
        isManual: !med.medicineId,
        tradeName: med.tradeName || "Unknown Medication",
        manufacturer: med.manufacturer || "",
        packSize: med.packSize || "",
        dosage: med.dosage || "As prescribed",
        form: med.form || "Tablet",
        startDate: moment(new Date()).format("DD-MM-YYYY"),
        finishDate: deriveFinishDate({
          startDate: moment(new Date()).format("DD-MM-YYYY"),
          duration: med.duration,
          durationFormat: med.durationFormat || "days",
        }),
        duration: med.duration ? med.duration.toString() : "",
        durationFormat: med.durationFormat || "days",
        usageType: med.usageType || "LONGTERM",
        repeat: med.repeat ? med.repeat.toString() : "",
        route: med.route || "",
        frequency: med.frequency || "",
        quantity: med.quantity ? med.quantity.toString() : "",
        medicineId: med.medicineId || "",
      }));
    }
    if (initialData.medicines && initialData.medicines.length > 0) {
      return initialData.medicines.map((med) => ({
        ...med,
        duration: med.duration != null ? med.duration.toString() : "",
        repeat: med.repeat != null ? med.repeat.toString() : "",
        packSize: med.packSize != null ? med.packSize.toString() : "",
        quantity: med.quantity != null ? med.quantity.toString() : "",
        startDate: med.startDate
          ? moment(med.startDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
            ? moment(med.startDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("DD-MM-YYYY")
            : med.startDate
          : moment().format("DD-MM-YYYY"),
        finishDate: med.finishDate
          ? moment(med.finishDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
            ? moment(med.finishDate, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("DD-MM-YYYY")
            : ""
          : "",
      }));
    }
    return initialMedications || [getDefaultMedicine()];
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      medicines: prepareMedicationsForRenewal(),
      instructions: initialData.instructions || "",
    },
  });

  const {
    fields,
    append,
    remove: removeField,
  } = useFieldArray({
    control: form.control,
    name: "medicines",
  });

  // Rows whose finish date the prescriber typed themselves. Those stop tracking the duration — an
  // explicit clinical decision outranks a derived one (KIN-1932).
  const manuallySetFinishDates = useRef(new Set());

  const watchedMedicines = form.watch("medicines");

  useEffect(() => {
    watchedMedicines?.forEach((medicine, index) => {
      if (manuallySetFinishDates.current.has(index)) {
        return;
      }
      const derived = deriveFinishDate({
        startDate: medicine.startDate,
        duration: medicine.duration,
        durationFormat: medicine.durationFormat,
      });
      if (derived !== medicine.finishDate) {
        form.setValue(`medicines.${index}.finishDate`, derived);
      }
    });
  }, [watchedMedicines, form]);

  // Custom remove that also clears allergies and re-indexes
  const remove = (index) => {
    removeField(index);
    manuallySetFinishDates.current = new Set(
      [...manuallySetFinishDates.current]
        .filter((manualIndex) => manualIndex !== index)
        .map((manualIndex) => (manualIndex > index ? manualIndex - 1 : manualIndex))
    );
    setMedicationAllergies((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([key, value]) => {
        const keyNum = parseInt(key, 10);
        if (keyNum < index) {
          updated[keyNum] = value;
        } else if (keyNum > index) {
          updated[keyNum - 1] = value;
        }
        // Skip the removed index
      });
      return updated;
    });
  };

  if (!form) {
    console.error("Form is not initialized properly.");
    return null;
  }

  const handleSubmit = async () => {
    // Also guarded here, not just on the button: submitting on Enter must not
    // slip past an unacknowledged incomplete allergy check (KIN-1615).
    if (isBlockedOnAllergyCheck) return;

    const values = form.getValues();

    const isValid = await form.trigger();
    if (!isValid) {
      console.error("Form validation failed:", form.formState.errors);
      return;
    }

    const prescriptionData = {
      note: values.instructions,
      patientId: patientId,
      tenant: tenantVal,
      consultationId: consultationId,
      medications: values.medicines.map((item) => {
        const base = {
          consultationId: consultationId,
          id: item.id,
          dosage: item.dosage,
          form: item.form,
          startDate: moment(item.startDate, "DD-MM-YYYY").format("YYYY-MM-DD"),
          finishDate: hasFinishDate && item.finishDate
            ? moment(item.finishDate, "DD-MM-YYYY").format("YYYY-MM-DD")
            : null,
          route: item.route,
          duration: item.duration,
          durationFormat: item.durationFormat,
          frequency: item.frequency,
          acuteLongTerm: item.usageType,
          repeat: item.repeat ? Number(item.repeat) : null,
          quantity: item.quantity ? Number(item.quantity) : null,
          patientId: patientId,
          note: notesEnabled ? item.note : undefined,
        };

        if (item.isManual) {
          return {
            ...base,
            tradeName: item.tradeName,
            manufacturer: item.manufacturer,
            packSize: item.packSize,
          };
        }

        return { ...base, medicationId: item.medicineId };
      }),
    };

    try {
      let res;

      try {
        if (savedId.current) {
          res = await createPrescription.mutateAsync({
            id: savedId.current,
            ...prescriptionData,
          });
        } else {
          res = await createPrescription.mutateAsync(prescriptionData);
        }
      } catch (mutationError) {
        // Already logged and toasted by useCreatePrescription's onError.
        return;
      }

      savedId.current = res.id;
      onSuccess?.(res);
      await onSaved?.();

      if (!autoSave) {
        form.reset();
      }

      queryClient.invalidateQueries();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        "The prescription saved, but something went wrong afterward. Please refresh and check."
      );
    }
  };

  return (
    <>
      <FormProvider {...form}>
        {!isReadOnly && isDrugAllergiesError && (
          <DataLoadError
            label="this patient's recorded drug allergies"
            text="This is not a statement that the patient has none. Check the allergy record before issuing this prescription."
            onRetry={refetchDrugAllergies}
            isRetrying={isRefetchingDrugAllergies}
          />
        )}
        {!isReadOnly && drugAllergies && drugAllergies.length > 0 && (
          <div className={style.allergyWarningSection}>
            <div className={style.allergyWarningHeader}>
              <span className={style.warningIcon}>⚠️</span>
              <span className={style.allergyWarningTitle}>Drug Allergies:</span>
            </div>
            <ul className={style.allergyList}>
              {drugAllergies.map((allergy) => (
                <li key={allergy.id} className={style.allergyItem}>
                  <strong>{allergy.drugName || allergy.name}</strong>
                  {allergy.drugGenericName && (
                    <span> ({allergy.drugGenericName})</span>
                  )}
                  <span> - {allergy.allergyReaction}</span>
                  {allergy.serious && (
                    <span className={style.seriousFlag}> • SERIOUS</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("AddPrescriptionForm"))}
        >
          {isReadOnly && !initialData?.medicines?.length ? (
            <div className={style.emptyState}>
              <p className={style.emptyStateTitle}>No prescription found</p>
              <p className={style.emptyStateSubtitle}>No prescription was added to this consultation.</p>
            </div>
          ) : (
          <div
            className={`${style.medicineSectionWrapper} ${
              onCancel ? "" : style.medicineSectionWrapperWithoutBorder
            } ${isReadOnly ? style.medicineSectionWrapperNoScroll : ""}`}
          >
            {fields.map((field, index) => (
              <div key={field.id} className={style.medicineSection}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "15px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  ></div>
                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div className={style.drugSection}>
                        <SelectDrug
                          index={index}
                          form={form}
                          initialData={
                            initialData.medicines?.[index] ??
                            initialMedications?.[index]
                          }
                          onAllergyCheck={handleAllergyCheck}
                          isReadOnly={isReadOnly}
                        />
                      </div>
                    </div>
                    {index > 0 && !isReadOnly && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className={style.removeButton}
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                        aria-label="Remove medicine"
                      >
                        <Trash width={18} height={18} />
                      </button>
                    )}
                  </div>
                </div>

                <div className={style.inputGroupGrid}>
                  <FormField name={`medicines.${index}.dosage`}>
                    <label className={style.addLetterFormLabel}>Dosage</label>
                    <input
                      className="form-control"
                      readOnly={isReadOnly}
                      {...form.register(`medicines.${index}.dosage`)}
                    />
                  </FormField>

                  <FormField
                    name={`medicines.${index}.form`}
                    error={form.formState.errors.medicines?.[index]?.form}
                  >
                    <label className={style.addLetterFormLabel}>Form</label>
                    <input
                      className="form-control"
                      readOnly={isReadOnly}
                      {...form.register(`medicines.${index}.form`)}
                    />
                  </FormField>

                  <div>
                    <FormField name={`medicines.${index}.startDate`}>
                      <label className={style.addLetterFormLabel}>
                        Start date
                      </label>
                      <DatePickerField
                        value={
                          field.startDate
                            ? moment(field.startDate, "DD-MM-YYYY").format("YYYY-MM-DD")
                            : null
                        }
                        disabled={isReadOnly}
                        onChange={(dateStr) =>
                          form.setValue(
                            `medicines.${index}.startDate`,
                            dateStr ? moment(dateStr, "YYYY-MM-DD").format("DD-MM-YYYY") : ""
                          )
                        }
                      />
                    </FormField>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Switch
                        checked={hasFinishDate}
                        disabled={isReadOnly}
                        onChange={() => setHasFinishDate((prev) => !prev)}
                        sx={{
                          padding: "6px",
                          marginLeft: "-6px",
                          borderRadius: "16px",
                          "& .MuiSwitch-switchBase": {
                            "&.Mui-checked": {
                              color: "#fff",
                              "& + .MuiSwitch-track": {
                                backgroundColor: "var(--green-800)",
                                opacity: 1,
                              },
                            },
                          },
                          "& .MuiSwitch-thumb": {
                            backgroundColor: "#fff",
                          },
                          "& .MuiSwitch-track": {
                            backgroundColor: "#a0a0a0",
                            opacity: 0.5,
                            padding: 0,
                            borderRadius: "16px",
                          },
                        }}
                      />
                      <span style={{ fontSize: "15px", marginTop: "2px" }}>
                        {hasFinishDate ? "Finish day" : "Add finish day"}
                      </span>
                    </div>
                  </div>
                  {hasFinishDate ? (
                    <FormField name={`medicines.${index}.finishDate`}>
                      <label className={style.addLetterFormLabel}>
                        Finish date
                      </label>
                      <DatePickerField
                        // Read from the live form value, not the useFieldArray snapshot: `fields` only
                        // refreshes on append/remove, so a date derived from the duration would not show
                        // until the row was re-rendered for another reason (KIN-1932).
                        value={
                          watchedMedicines?.[index]?.finishDate
                            ? moment(
                                watchedMedicines[index].finishDate,
                                "DD-MM-YYYY"
                              ).format("YYYY-MM-DD")
                            : null
                        }
                        disabled={isReadOnly}
                        onChange={(dateStr) => {
                          manuallySetFinishDates.current.add(index);
                          form.setValue(
                            `medicines.${index}.finishDate`,
                            dateStr ? moment(dateStr, "YYYY-MM-DD").format("DD-MM-YYYY") : ""
                          );
                        }}
                      />
                    </FormField>
                  ) : (
                    <div />
                  )}
                  {
                    <FormField name={`medicines.${index}.endDate`}>
                      <label className={style.addLetterFormLabel}>Route</label>
                      <Select
                        name={`medicines.${index}.route`}
                        options={[
                          { label: "Aural", value: "Aural" },
                          { label: "Inhalation", value: "Inh" },
                          { label: "Intramuscular", value: "IM" },
                          { label: "Intranasal", value: "Intranasal" },
                          { label: "Intravenous", value: "IV" },
                          { label: "PO", value: "PO" },
                          { label: "PR", value: "PR" },
                          { label: "PV", value: "PV" },
                          { label: "Subcutaneous", value: "SC" },
                          { label: "Sublingual", value: "Sublingual" },
                          { label: "Topical", value: "Top" },
                        ]}
                        value={form.watch(`medicines.${index}.route`)}
                        onChange={(value) =>
                          form.setValue(`medicines.${index}.route`, value)
                        }
                        fullWidth
                        disabled={isReadOnly}
                      />
                    </FormField>
                  }
                  <FormField name={`medicines.${index}.duration`}>
                    <label className={style.addLetterFormLabel}>
                      Duration (optional)
                    </label>
                    <input
                      className="form-control"
                      inputMode="text"
                      pattern="[0-9]*"
                      maxLength={4}
                      readOnly={isReadOnly}
                      {...form.register(`medicines.${index}.duration`, {
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                        },
                      })}
                    />
                  </FormField>

                  <FormField name={`medicines.${index}.durationFormat`}>
                    <label className={style.addLetterFormLabel}>
                      Duration format (optional)
                    </label>
                    <Select
                      name={`medicines.${index}.durationFormat`}
                      options={[
                        { label: "Days", value: "days" },
                        { label: "Months", value: "months" },
                      ]}
                      value={form.watch(`medicines.${index}.durationFormat`)}
                      onChange={(value) =>
                        form.setValue(
                          `medicines.${index}.durationFormat`,
                          value
                        )
                      }
                      placeholder={null}
                      fullWidth
                      disabled={isReadOnly}
                    />
                  </FormField>

                  {
                    <FormField name={`medicines.${index}.endDate`}>
                      <label className={style.addLetterFormLabel}>
                        Frequency
                      </label>
                      <Select
                        name={`medicines.${index}.frequency`}
                        options={[
                          { label: "STAT", value: "STAT" },
                          { label: "OD - Once Daily", value: "OD" },
                          { label: "BD - Twice Daily", value: "BD" },
                          { label: "Three times daily", value: "TDS" },
                          { label: "Four times daily", value: "QDS" },
                          { label: "As required", value: "PRN" },
                          { label: "Once Weekly", value: "Once Weekly" },
                          { label: "Twice Weekly", value: "Twice Weekly" },
                          {
                            label: "Three time weekly",
                            value: "Three time weekly",
                          },
                        ]}
                        value={form.watch(`medicines.${index}.frequency`)}
                        onChange={(value) =>
                          form.setValue(`medicines.${index}.frequency`, value)
                        }
                        fullWidth
                        disabled={isReadOnly}
                      />
                    </FormField>
                  }

                  {
                    <FormField name={`medicines.${index}.quantity`}>
                      <label className={style.addLetterFormLabel}>
                        Quantity (optional)
                      </label>
                      <input
                        className="form-control"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        readOnly={isReadOnly}
                        {...form.register(`medicines.${index}.quantity`, {
                          onChange: (e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                          },
                        })}
                      />
                    </FormField>
                  }

                  <FormField name={`medicines.${index}.usageType`}>
                    <label className={style.addLetterFormLabel}>
                      Usage type (optional)
                    </label>
                    <Select
                      name={`medicines.${index}.usageType`}
                      options={[
                        { label: "Acute", value: "ACUTE" },
                        { label: "Long term", value: "LONGTERM" },
                      ]}
                      value={form.watch(`medicines.${index}.usageType`)}
                      onChange={(value) =>
                        form.setValue(`medicines.${index}.usageType`, value)
                      }
                      placeholder={null}
                      fullWidth
                      disabled={isReadOnly}
                    />
                  </FormField>

                  {form.watch(`medicines.${index}.usageType`) ===
                    "LONGTERM" && (
                    <FormField name={`medicines.${index}.repeat`}>
                      <label className={style.addLetterFormLabel}>
                        Repeat (optional)
                      </label>
                      <input
                        className="form-control"
                        placeholder="How many times?"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={2}
                        readOnly={isReadOnly}
                        {...form.register(`medicines.${index}.repeat`, {
                          onChange: (e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                          },
                        })}
                      />
                      <p className={style.helperText}>
                        Number of times this prescription can be reissued
                        without a new consultation.
                      </p>
                    </FormField>
                  )}

                  {notesEnabled && (
                    <div className={style.noteField}>
                      <FormField name={`medicines.${index}.note`}>
                        <label className={style.addLetterFormLabel}>
                          Note (optional)
                        </label>
                        <textarea
                          className="form-control textareaform-control"
                          rows={2}
                          readOnly={isReadOnly}
                          {...form.register(`medicines.${index}.note`)}
                        />
                      </FormField>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => append(getDefaultMedicine())}
              className={style.addAnotherMedicine}
            >
              Add another medicine +
            </button>
          )}
          {(!isReadOnly || !!initialData?.instructions) && (
            <FormField name="instructions">
              <label className={style.addLetterFormLabel}>
                Instructions (optional)
              </label>
              <textarea
                className="form-control textareaform-control"
                style={{ minHeight: "126px" }}
                readOnly={isReadOnly}
                {...form.register("instructions")}
              />
            </FormField>
          )}
          {uncheckedDrugNames.length > 0 && (
            <DataLoadError
              label={`the allergy check for ${uncheckedDrugNames.join(", ")}`}
              text="No allergy was found because the check did not complete, not because the patient has none."
              onRetry={retryFailedAllergyChecks}
            />
          )}
          {needsAllergyAcknowledgement && (
            <label className={style.allergyAcknowledgement}>
              <Checkbox
                checked={hasAcknowledgedUncheckedAllergies}
                handleChange={(e) =>
                  setHasAcknowledgedUncheckedAllergies(e.target.checked)
                }
              />
              I understand the allergy check could not be completed and have
              verified this patient's allergies another way.
            </label>
          )}
          <div
            className={`${style.bottomSection} ${
              onCancel ? "" : style.bottomSectionWithoutBorder
            }`}
          >
            <label className={style.formCheckboxLabel}></label>
            <div>
              {!isReadOnly && (
                <>
                  {onCancel && (
                    <Button
                      variant="outline"
                      size="large"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel?.();
                      }}
                      type="button"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="large"
                    type="button"
                    onClick={form.handleSubmit(handleSubmit, onInvalidSubmit("AddPrescriptionForm"))}
                    disabled={
                      createPrescription.isPending || isBlockedOnAllergyCheck
                    }
                  >
                    {createPrescription.isPending ? "Submitting..." : "Save prescription"}
                  </Button>
                  {initialData.filePath && initialData.id && (
                    <>
                      <Button
                        variant="outline"
                        size="large"
                        type="button"
                        onClick={() =>
                          printPrescription(initialData.id, tenantVal, patientId)
                        }
                      >
                        Print prescription
                      </Button>
                      <Button
                        variant="outline"
                        size="large"
                        type="button"
                        onClick={() =>
                          downloadPrescription(initialData.id, tenantVal, patientId)
                        }
                      >
                        Download prescription
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          {selectedMedicationAllergies.length > 0 && (
            <div className={style.allergyWarningSection}>
              <div className={style.allergyWarningHeader}>
                <span className={style.warningIcon}>⚠️</span>
                <span className={style.allergyWarningTitle}>
                  Warning: Selected medications may cause allergic reactions
                </span>
              </div>
              <ul className={style.allergyList}>
                {selectedMedicationAllergies.map((allergy) => (
                  <li key={allergy.id} className={style.allergyItem}>
                    <strong>{allergy.drugName || allergy.name}</strong>
                    {allergy.drugGenericName && (
                      <span> ({allergy.drugGenericName})</span>
                    )}
                    {allergy.allergyReaction && (
                      <span> - {allergy.allergyReaction}</span>
                    )}
                    {allergy.ingredients && (
                      <span> • Ingredients: {allergy.ingredients}</span>
                    )}
                    {allergy.serious && (
                      <span className={style.seriousFlag}> • SERIOUS</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>

      </FormProvider>
    </>
  );
};
